import React, { useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Activity, Package, FileText, Repeat, 
  Users, Zap, Target, ArrowRight,
  Stethoscope, Factory, ShoppingCart, 
  Truck, Radio, DollarSign
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const SoftwareDevelopment = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
            entry.target.classList.remove('opacity-0');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.scroll-reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const services = [
    {
      title: 'Website Development',
      description: 'We craft responsive, SEO-optimized, and AI-integrated websites tailored to your goals. From marketing sites to intelligent dashboards — we deliver clean, scalable code wrapped in stunning UX.'
    },
    {
      title: 'E-commerce Development',
      description: 'We build next-gen e-commerce platforms powered by intelligent recommendations, secure payment integration, and frictionless customer experience. Convert visitors into loyal users.'
    },
    {
      title: 'CMS Development',
      description: 'We design powerful, flexible CMS solutions — from headless architecture to content automation using AI. Manage your content seamlessly, without compromising performance.'
    }
  ];

  const industries = [
    { icon: Stethoscope, name: 'Healthcare' },
    { icon: Factory, name: 'Manufacturing' },
    { icon: ShoppingCart, name: 'Retail & Wholesale' },
    { icon: Truck, name: 'Transportation & Logistics' },
    { icon: Radio, name: 'Telecommunications' },
    { icon: DollarSign, name: 'Finance & Insurance' }
  ];

  const agileSteps = [
    'Plan',
    'Design',
    'Develop',
    'Test',
    'Launch',
    'Review',
    'Deploy'
  ];

  const whyChoose = [
    {
      icon: Target,
      title: 'End-to-End Expertise',
      description: 'We manage every phase — from ideation to deployment — ensuring reliability, performance, and measurable business impact. Our focus is scalability, clean architecture, and intelligent optimization.'
    },
    {
      icon: Repeat,
      title: 'Agile-Driven Approach',
      description: 'We combine traditional agile principles with AI-enhanced analytics for smarter iterations, faster feedback, and adaptive delivery that matches your market needs.'
    },
    {
      icon: Users,
      title: 'Industry-Aligned Solutions',
      description: 'We design intelligent solutions that speak the language of your industry — whether healthcare, logistics, or finance — embedding domain knowledge into every build.'
    }
  ];

  return (
    <div className="bg-gray-50 text-gray-900 overflow-hidden">
      <Navbar />

      {/* Hero Section */}
      <section className="min-h-[50vh] flex items-center justify-center relative px-6 lg:px-12 pt-24 pb-12">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-100 to-white">
          <div className="absolute inset-0 opacity-30">
            <div
              className="w-full h-full"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233B82F6' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                backgroundSize: '60px 60px'
              }}
            ></div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/50 to-white"></div>
        </div>

        <div className="max-w-5xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-light tracking-tight mb-8 leading-[1.1] text-gray-900 animate-fade-in" style={{ fontWeight: 300 }}>
            Software Development
            <br />
            <span className="text-blue-600">Services</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-12 leading-relaxed font-light animate-fade-in" style={{ animationDelay: '200ms' }}>
            We craft intelligent software that solves real-world challenges. From architecture to AI integration,
            our end-to-end software development services deliver speed, scalability, and impact — from planning to deployment.
          </p>
          <div className="relative inline-block group">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-sky-500 rounded-xl blur opacity-30 group-hover:opacity-60 transition-opacity duration-500"></div>
            <Button
              size="lg"
              className="relative bg-gradient-to-r from-blue-500 to-sky-500 hover:from-sky-500 hover:to-blue-500 text-white px-8 py-6 shadow-lg hover:shadow-2xl transition-all duration-300"
            >
              <span className="relative z-10">Get Free Consultation</span>
              <div className="absolute inset-0 rounded-xl overflow-hidden">
                <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
              </div>
            </Button>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-32 px-6 lg:px-12 bg-white relative overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="scroll-reveal opacity-0">
            <p className="text-blue-500 text-sm uppercase tracking-wider mb-4">Overview</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 leading-tight">
              What is Software Development?
            </h2>
            <div className="space-y-6 text-gray-600 text-lg leading-relaxed">
              <p>
                Software development at ThinkVerse.AI isn't just about code — it's about intelligence. We design, develop, and deploy systems and applications that enhance user experience, automate processes, and solve complex business problems.
                Our agile-driven approach turns concepts into high-performance products through structured planning, design thinking, rapid iteration, and continuous support.
              </p>
              <p>
                By bridging the gap between innovation and usability, we create cloud-native, enterprise-grade, and AI-augmented solutions. We enable companies to scale faster, automate smarter, and stay competitive in a world driven by intelligence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Differentiator Section */}
      <section className="py-32 px-6 lg:px-12 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20 scroll-reveal opacity-0">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Create Software That
              <br />
              <span className="text-blue-500">Makes a Difference</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Our team engineers scalable, secure, and adaptive software that grows with your business. We combine creativity with deep AI and engineering expertise to deliver performance-driven, future-ready solutions.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {services.map((service, index) => (
              <Card
                key={index}
                className="bg-white border-gray-30000 hover:border-blue-500/50 backdrop-blur-sm transition-all duration-300 hover:scale-105 scroll-reveal opacity-0"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 text-gray-900">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center scroll-reveal opacity-0">
            <Button
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-gray-900 px-8 py-6 shadow-[0_0_30px_rgba(53,122,189,0.4)] hover:shadow-[0_0_40px_rgba(74,144,226,0.6)] transition-all duration-300"
            >
              Get in Touch with Us <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* Agile Methodology Section */}
      <section className="py-32 px-6 lg:px-12 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            {/* Left Content */}
            <div className="scroll-reveal opacity-0">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Agile
                <br />
                <span className="text-blue-500">Methodology</span>
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                At ThinkVerse.AI, agility drives innovation. Our AI-enhanced agile model ensures rapid prototyping, iterative design, and constant feedback loops — giving your product a purposeful evolution.
                From planning to deployment, our process keeps you informed, adaptive, and ahead.
              </p>
            </div>

            {/* Right Visual - Agile Cycle */}
            <div className="scroll-reveal opacity-0 flex items-center justify-center">
              <div className="relative w-80 h-80">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-blue-500/20 rounded-full blur-2xl animate-pulse"></div>
                <div className="absolute inset-10 border-4 border-blue-500/40 rounded-full animate-spin-slow"></div>
                <div className="absolute inset-20 border-4 border-blue-500/40 rounded-full animate-spin-reverse"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <Repeat size={60} className="text-blue-500 mx-auto mb-4" />
                    <div className="space-y-1">
                      {agileSteps.map((step, index) => (
                        <div key={index} className="text-xs text-gray-600">
                          {step}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Solutions Section */}
      <section className="py-32 px-6 lg:px-12 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            {/* Left - Industries List */}
            <div className="scroll-reveal opacity-0">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Offering Industry-Specific
                <br />
                <span className="text-blue-500">Software Services</span>
              </h2>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                We deliver tailored software solutions designed to solve industry-specific challenges.
                Our domain expertise helps enterprises modernize, streamline operations, and drive growth through intelligent transformation.
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                {industries.map((industry, index) => {
                  const Icon = industry.icon;
                  return (
                    <div
                      key={index}
                      className="flex items-center space-x-3 p-4 bg-white rounded-lg border border-gray-30000 hover:border-blue-500/50 transition-all duration-300"
                    >
                      <Icon size={24} className="text-blue-500" />
                      <span className="text-gray-700">{industry.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right - Visual Placeholder */}
            <div className="scroll-reveal opacity-0">
              <div className="relative w-full h-96 bg-gradient-to-br from-[#071952]/50 to-blue-600/50 rounded-lg overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <Activity size={80} className="text-blue-500 mx-auto mb-4 opacity-50" />
                    <p className="text-gray-400">Industry Collaboration Visual</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Custom Solution CTA Section */}
      <section className="py-32 px-6 lg:px-12 bg-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-r from-[#071952] to-blue-600"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10 scroll-reveal opacity-0">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Need a Custom Software
            <br />
            <span className="text-blue-500">Development Partner?</span>
          </h2>
          <p className="text-gray-600 text-lg mb-8 leading-relaxed">
            At ThinkVerse.AI, we provide enterprise-grade, cost-effective software powered by intelligence.
            From startups to global enterprises, our team helps you innovate faster and scale smarter.
          </p>
          <Button
            size="lg"
            className="bg-blue-500 hover:bg-[#4A6EEE] text-gray-900 px-10 py-6 shadow-[0_0_30px_rgba(74,144,226,0.4)] hover:shadow-[0_0_40px_rgba(74,144,226,0.6)] transition-all duration-300"
          >
            Schedule a Consultation Call
          </Button>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-32 px-6 lg:px-12 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 scroll-reveal opacity-0">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Why Choose ThinkVerse.AI for
              <br />
              <span className="text-blue-500">Software Development?</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {whyChoose.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="scroll-reveal opacity-0 p-8 bg-white border border-gray-30000 rounded-lg hover:border-blue-500/50 transition-all duration-300"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="mb-6">
                    <Icon size={40} className="text-blue-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 bg-gradient-to-r from-blue-500 to-blue-500 bg-clip-text text-transparent">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SoftwareDevelopment;
